// src/components/NavBar.jsx
import {
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Fade,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { menuItems } from "../config/menuItems";
import StyledAppBar from "./StyledAppBar";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "./common/modals/ConfirmDialog";
import HomeFab from "./common/controls/HomeFab";
import { useHomeFab } from "../context/HomeFabContext";
import appHeaderImage from "../app-header.png";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ProfileInfo from "./settings/modals/ProfileInfo";

export default function NavBar() {
  const { t } = useTranslation("common");
  const { isAuthenticated, role, handleLogout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { disableHomeFab, setDisableHomeFab } = useHomeFab();

  const appName = process.env.REACT_APP_NAME || "My App";
  const filteredItems = menuItems.filter((item) => item.roles.includes(role));

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setDisableHomeFab(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setDisableHomeFab(false);
  };

  const onLogoutButtonClick = () => {
    handleLogout();
    handleMenuClose();
  };

  return (
    <>
      <StyledAppBar position="sticky">
        <Toolbar>
          <Box
            component="div"
            sx={{
              height: 40,
              width: "auto",
              mr: 2,
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={appHeaderImage}
              alt="Animora Vet Hub"
              sx={{ height: 34, width: "auto", mr: 2 }}
            />
          </Box>

          {/* Desktop buttons */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            {isAuthenticated &&
              filteredItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  component={Link}
                  to={item.path}
                >
                  {item.label}
                </Button>
              ))}

            {isAuthenticated ? (
              <Button color="inherit" onClick={() => setConfirmDialogOpen(true)}>
                Logout
              </Button>
            ) : (
              <Button color="inherit" component={Link} to="/login">
                Login
              </Button>
            )}
          </Box>

          {/* Mobile menu */}
          <Box sx={{ display: { xs: "block", md: "none" }, display: "flex", alignItems: "center" }}>
            {/* Profile icon */}
            <ProfileInfo mode="button" />

            {/* Menu icon */}
            <IconButton color="inherit" onClick={handleMenuOpen}>
              <MenuIcon />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 300 }}
              PaperProps={{
                sx: { 
                  width: 200,
                  px: 1.5,
                  py: 1.5,
                  mt: 2.5,
                  borderRadius: 2,
                },
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              {isAuthenticated &&
                filteredItems.map((item) => (
                  <MenuItem
                    key={item.path}
                    component={Link}
                    to={item.path}
                    onClick={handleMenuClose}
                    sx={{ py: 1.2, px: 2 }}
                  >
                    <item.icon fontSize="small" sx={{ color: "secondary.main" }} style={{ marginRight: 8 }} />
                    {item.label}
                  </MenuItem>
                ))}

              {isAuthenticated ? (
                <MenuItem onClick={() => setConfirmDialogOpen(true)} sx={{ py: 1.2, px: 2 }}>
                  <LogoutIcon fontSize="small" sx={{ color: "secondary.main" }} style={{ marginRight: 8 }} />
                  {t("buttons.logout")}
                </MenuItem>
              ) : (
                <MenuItem component={Link} to="/login" onClick={handleMenuClose} sx={{ py: 1.2, px: 2 }}>
                  Login
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Toolbar>
      </StyledAppBar>
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Logout"
        content="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        onConfirm={onLogoutButtonClick}
        onCancel={() => setConfirmDialogOpen(false)}
      />

      {isAuthenticated ? <HomeFab disabled={disableHomeFab} /> : null}
    </>
  );
}
